import { Inject, Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogService, SelectExportMetadataComponent } from '@serviceplan/ui-kit';
import { AssetBasketService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  BackgroundProcessService,
  ClientEnvironment,
  ENVIRONMENT,
  FilterAllOptions,
  FilterFormDataModel,
  FormControlModel,
  MetadataModel,
  ScreenSize,
} from '@thehubz/client/core';
import { Asset, ExportExcelRequest, ExportsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, BuyoutRights, BuyoutRightsSeat, MULTI_VALUE } from '@thehubz/shared/model';
import { EMPTY, Observable, of, Subject, switchMap } from 'rxjs';
import { SeatMandatoryField, SeatNonMandatoryField } from '../../../customer/seat/seat-export-metadata-fields';
import { MandatoryField, NonMandatoryField } from '../../../customer/standard/export-metadata-fields';
import { VorwerkMandatoryField, VorwerkNonMandatoryField } from '../../../customer/vorwerk/vorwerk-export-metadata-fields';
import { AssetCommonService } from '../../../services/asset-common.service';
import { AssetViewTypeService, GridConfig } from '../../../services/asset-view-type.service';
import { ProductBrandSelectionService } from '../../../services/product-brand-selection.service';
import { BuyoutUtilsService } from '../../asset-buyout-rights/buyout-utils.service';
import { buyoutRightsFields, buyoutRightsFieldsSeat } from '../../asset-upload/stepper-config/asset-settings-stepper.constants';
import { isExcludedKey } from '../assets-page.util';
import { AssetsSidebarComponent } from '../assets-sidebar/assets-sidebar.component';

export interface BuyoutRightsType {
  [key: string]: {
    similarBuyout: Map<string, AnyType>;
    differentBuyout: Map<string, AnyType>;
  };
}

export interface ProjectionDataModel {
  excludedFields?: Record<string, number>;
  includedFields?: Record<string, number>;
  extraFields?: Record<string, number>;
  buyoutRightsFields?: string[];
  domainFlag?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AssetsPageService {
  brandingKey: CustomClients;
  filterObject: FilterAllOptions = {
    filters: {},
    sort: {},
    limit: 48,
    skip: 0,
  };
  usageRightUpdated$ = new Subject<boolean>();

  buyoutRightsTypes!: BuyoutRightsType;

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private confirmDialog: ConfirmDialogService,
    private dialog: MatDialog,
    private assetCommonService: AssetCommonService,
    private buyoutUtilsService: BuyoutUtilsService,
    private assetBasketService: AssetBasketService,
    private exportExcelApiService: ExportsApiService,
    private assetViewTypeService: AssetViewTypeService,
    private backgroundProcessService: BackgroundProcessService,
    private productBrandSelectionService: ProductBrandSelectionService,
  ) {
    this.brandingKey = this.env.brandingKey;
  }

  /**
   * Update the asset session storage with the updated assets
   * @param updatedAssets
   */
  updateAssetSessionStorage(updatedAssets: AssetUIModel[]) {
    // Parse the selected items from the session storage
    if (sessionStorage[AppSettings.SESSION_STORAGE_KEY]) {
      const selectedItems = JSON.parse(sessionStorage[AppSettings.SESSION_STORAGE_KEY]);
      // Find the index of the 'assets' type in the selected items
      const assetTypeIndex = selectedItems.findIndex((obj: { type: string }) => obj.type === 'assets');
      const selectedAssetItems = selectedItems[assetTypeIndex];
      if (selectedAssetItems?.items?.length > 0 && updatedAssets.length > 0) {
        updatedAssets.forEach((item: AssetUIModel) => {
          const updateIndex = selectedAssetItems.items.findIndex((asset: AssetUIModel) => asset._id === item._id);
          if (updateIndex === -1 && item.isAddedToBasket) {
            selectedAssetItems.items.push(item);
          } else if (updateIndex !== -1 && item.isAddedToBasket) {
            selectedAssetItems.items[updateIndex] = item;
          }
        });
      }
      sessionStorage.setItem(AppSettings.SESSION_STORAGE_KEY, JSON.stringify(selectedItems));
    }
  }

  /**
   * Checks if the dialog edit state is invalid
   */
  isDialogEditStateInvalid(dialogEditState?: number): boolean {
    // Return true if the dialog edit state is not defined or is not equal to 0
    return !dialogEditState && dialogEditState != 0;
  }

  /**
   * Checks if the branding key is supported
   * @returns
   */
  isSupportedBranding(): boolean {
    // Define the supported brands
    const supportedBrands = [CustomClients.Vorwerk, CustomClients.Standard, CustomClients.SEAT];
    // Return true if the branding key is included in the supported brands
    return supportedBrands.includes(this.brandingKey);
  }

  /**
   * Handles the editing of usage rights for the provided assets
   * @param assets
   */
  handleUsageRightsEdit(assets: AssetUIModel[]): void {
    // Check if the number of assets is equal to 1
    if (assets.length === 1) {
      // Process the single asset
      this.processSingleAsset(assets[0]);
    } else {
      // Process multiple assets
      this.processMultipleAssets(assets);
    }
  }

  /**
   * Processes the editing of a single asset
   * @param asset
   */
  processSingleAsset(asset: AssetUIModel): void {
    // Set the asset data subject to the provided asset
    this.assetCommonService.assetDataSubject$.next(asset);
    const buyoutRightsComparison = this.compareBuyoutRightsTypes([asset]);
    // Open the asset buyout rights summary dialog for the provided asset
    this.buyoutUtilsService.assetBuyoutRightsSummaryDialogs([asset], buyoutRightsComparison?.allTilesUsedFlag).subscribe((updated: boolean) => {
      // this.updateAssetSessionStorage(updated)
      this.usageRightUpdated$.next(updated);
    });
  }

  /**
   * Processes the editing of multiple assets
   * @param assets
   */
  processMultipleAssets(assets: AssetUIModel[]): void {
    // Compare the buyout rights types of the provided assets
    const buyoutRightsComparison = this.compareBuyoutRightsTypes(assets);
    // Check if the buyout rights have common types
    if (buyoutRightsComparison?.haveCommonTypes || buyoutRightsComparison?.unCommonTypes?.length) {
      // Process the common buyout rights for the provided assets
      this.processCommonBuyoutRights(assets, buyoutRightsComparison.unCommonTypes, buyoutRightsComparison?.allTilesUsedFlag);
    } else {
      this.assetCommonService.assetDataSubject$.next(assets[0]);
      this.handleAssetNotes(assets);
      this.buyoutUtilsService
        .assetBuyoutRightsSummaryDialogs(assets, buyoutRightsComparison?.allTilesUsedFlag)
        .subscribe((updated: boolean) => this.usageRightUpdated$.next(updated));
    }
  }

  /**
   * Compare buyout rights types among assets and categorize them into common and uncommon types
   * @param assets
   * @returns
   */
  compareBuyoutRightsTypes(assets: AssetUIModel[]) {
    // Check if there is at least one object in the array
    if (assets.length === 0) return undefined;
    // Initialize a flag to track if there are any common types
    let haveCommonTypes = false;
    let allTilesUsedFlag = false;
    // Create an object to store the count of each buyout rights type
    let BuyoutRightsObject = BuyoutRights;
    if (this.brandingKey === CustomClients.SEAT) {
      BuyoutRightsObject = BuyoutRightsSeat;
    }
    BuyoutRightsObject.forEach((obj) => {
      this.buyoutRightsTypes = {
        ...this.buyoutRightsTypes,
        [obj._id]: {
          similarBuyout: new Map<string, AnyType>(),
          differentBuyout: new Map<string, AnyType>(),
        },
      };
    });
    const buyoutTypesObj: Record<string, number> = BuyoutRightsObject.reduce((obj, type) => ({ ...obj, [type._id]: 0 }), {});
    // Initialize arrays to store common and uncommon types
    const commonTypes: string[] = [];
    const unCommonTypes: string[] = [];
    // Iterate through each asset in the input array
    assets.forEach((asset) => {
      // Iterate through each usage right of the current asset
      asset.usageRights?.forEach((rights) => {
        // Increment the count of the current buyout rights type
        buyoutTypesObj[rights.buyoutRightsTitle] = buyoutTypesObj[rights.buyoutRightsTitle] + 1;
      });
    });
    // Iterate through each key in the buyout types object
    for (const key in buyoutTypesObj) {
      // Check if the current key has a non-zero count
      if (buyoutTypesObj[key] > 0) {
        // Check if the current key has a count equal to the number of assets
        if (buyoutTypesObj[key] === assets.length) {
          // Add the current key to the common types array
          commonTypes.push(key);
          // Set the haveCommonTypes flag to true
          haveCommonTypes = true;
        } else {
          // Add the current key to the uncommon types array
          unCommonTypes.push(key);
        }
      }
    }
    const alreadyUsedTile = [...commonTypes, ...unCommonTypes];
    if (alreadyUsedTile.length === BuyoutRightsObject.length) {
      allTilesUsedFlag = true;
    }
    // Return an object containing the haveCommonTypes flag, common types array, and uncommon types array
    return { haveCommonTypes, commonTypes, unCommonTypes, allTilesUsedFlag };
  }

  /**
   * Processes the common buyout rights for the provided assets
   * @param assets
   * @param uncommonTypes
   */
  processCommonBuyoutRights(assets: AssetUIModel[], uncommonTypes: string[], allTilesUsedFlag?: boolean): void {
    // Remove the uncommon usage rights from the provided assets
    this.removeUnCommonUsageRights(assets, uncommonTypes);
    // Get the first asset's usage rights
    const firstAssetUsageRights = assets[0].usageRights as AnyType;
    // Update the usage rights of the other assets with the first asset's usage rights
    assets.slice(1).forEach((asset) => {
      this.updateUsageRights(firstAssetUsageRights || [], asset.usageRights || []);
    });
    // Set the asset data subject to the first asset
    this.assetCommonService.assetDataSubject$.next(assets[0]);
    // Set the unmatched usage right to the uncommon types
    this.assetCommonService.unMatchedUsageRight$.next(uncommonTypes);

    this.handleAssetNotes(assets);

    // Open the asset buyout rights summary dialog for the provided assets
    this.buyoutUtilsService.assetBuyoutRightsSummaryDialogs(assets, allTilesUsedFlag).subscribe((updated: boolean) => this.usageRightUpdated$.next(updated));
  }

  handleAssetNotes(assets: AssetUIModel[]) {
    const notesString = assets.filter((asset) => asset.notes !== undefined).map((asset) => asset.notes || '');
    const isAllNotesEqual = this.areNotesContentsEqual(notesString);
    if (isAllNotesEqual) {
      assets[0].notes = notesString[0];
    } else {
      assets[0].notes = MULTI_VALUE;
    }
  }

  areNotesContentsEqual(htmlArray: string[]): boolean {
    const getText = (html: string): string => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent?.trim() ?? '';
    };

    const textArray = htmlArray.map(getText);
    return textArray.every((text) => text === textArray[0]);
  }

  /**
   * Remove uncommon usage rights from the selected assets
   * @param selectedAssets
   * @param unCommonTypes
   */
  removeUnCommonUsageRights(selectedAssets: AssetUIModel[], unCommonTypes: string[]) {
    if (!unCommonTypes.length) return;
    // Iterate through each asset in the selected assets array
    selectedAssets.forEach((asset) => {
      // Initialize an array to store the filtered usage rights
      const usageRights = asset.usageRights || [];
      // Iterate through each uncommon usage right type
      unCommonTypes.forEach((type) => {
        // Find the index of the usage right with the matching type
        const index = usageRights.findIndex((usageRight) => usageRight.buyoutRightsTitle === type);
        // If the usage right is found, remove it from the array
        if (index !== -1) {
          usageRights.splice(index, 1);
        }
      });
    });
  }

  /**
   * Updates the usage rights of the provided asset with the provided usage rights
   * @param firstAssetUsageRights
   * @param restAssetUsageRights
   */
  updateUsageRights(firstAssetUsageRights: AnyType, restAssetUsageRights: AnyType): void {
    // Transform the buyout rights object of the first asset's usage rights
    const oriModifiedBuyoutRightsObj = this.transformBuyoutRightsObject(firstAssetUsageRights);
    // Transform the buyout rights object of the provided usage rights
    const restModBuyoutRightsObj = this.transformBuyoutRightsObject(restAssetUsageRights);

    // Iterate through each usageRight in the provided usageRights and update the corresponding fields in the first asset's usage rights
    restAssetUsageRights.forEach((right: AnyType) => {
      this.updateUsageRightsFields(firstAssetUsageRights, right);
    });
    // Update unmatched buyout rights between the first asset's usage rights and the provided usage rights
    this.updateMatchedNUnmatchedBuyoutRights(firstAssetUsageRights, oriModifiedBuyoutRightsObj, restModBuyoutRightsObj);
  }

  /**
   * Opens a confirmation dialog to inform the user that the buyout rights are not matching
   */
  showBuyoutRightsMismatchDialog(): void {
    this.confirmDialog.open({
      title: $localize`Buyout rights are not matching`,
      message: $localize`For the selected assets the buyout rights are not matching.\n\nPlease select assets that share the same buyout right in order to multi edit them.`,
      cancelCaption: false,
    });
  }

  showAssetMismatchDialog(): void {
    this.confirmDialog.open({
      title: $localize`Asset status are not matching`,
      message: $localize`For the selected assets the status are not matching.\n\nPlease select assets that share the same status in order to multi edit them.`,
      cancelCaption: false,
    });
  }

  /**
   * Transform buyout rights object into a more structured format
   * @param input
   * @returns
   */
  transformBuyoutRightsObject(input: AnyType[]) {
    // Initialize the output object
    const output: AnyType = {};
    // Iterate through each item in the input array
    input.forEach((item) => {
      // Initialize a buyout rights object for the current item
      const buyoutRightsObject: AnyType = {};
      // Iterate through each buyout right in the current item
      item.buyoutRights.forEach((right: AnyType) => {
        // Generate a unique key for the current buyout right based on its license region,
        // channel, and buyout type
        const licenseRegionKey = `${right.licenseRegion}.${right.channel}.${right.buyoutType}`;
        // Add the current buyout right to the buyout rights object using the generated key
        buyoutRightsObject[licenseRegionKey] = {
          ...right,
        };
      });
      // Add the buyout rights object to the output object using the buyout rights title
      // as the key
      output[item.buyoutRightsTitle] = {
        buyoutRights: Object.entries(buyoutRightsObject).map(([licenseRegionKey, details]: [string, AnyType]) => ({
          // Include the license region key and the details of each buyout right in the output
          licenseRegionKey,
          ...details,
        })),
      };
    });
    // Return the transformed output object
    return output;
  }

  /**
   * Updates the buyout rights fields of the first asset's usage rights based on the provided right
   * @param firstAssetUsageRights
   * @param right
   */
  updateUsageRightsFields(firstAssetUsageRights: AnyType, right: AnyType): void {
    // Find the index of the object in the first asset's usage rights with the same buyout rights title as the provided right
    const index = firstAssetUsageRights.findIndex((obj: AnyType) => obj.buyoutRightsTitle === right.buyoutRightsTitle);
    // Get the current buyout type and the corresponding buyout right fields
    const currentBuyoutType = firstAssetUsageRights[index].buyoutRightsTitle;
    let buyoutRightFields: FormControlModel[] = [];
    if (this.brandingKey === CustomClients.SEAT) {
      buyoutRightFields = this.getBuyoutRightFieldsSEAT(currentBuyoutType);
    } else {
      buyoutRightFields = this.getBuyoutRightFields(currentBuyoutType);
    }
    // Create an array of buyout right field form field names and include the buyout rights title
    const buyoutRightFieldsObj = buyoutRightFields.map((field) => field.formFieldName);
    buyoutRightFieldsObj.push('buyoutRightsTitle');
    // If the index is not -1 (meaning the object was found), iterate through each key in the buyout right fields object
    if (index !== -1) {
      buyoutRightFieldsObj.forEach((key) => {
        // If the key is not excluded and
        // the value of the key in the first asset's usage rights is not equal to the value of the key in the provided right,
        // set the value to MULTI_VALUE
        if (Array.isArray(firstAssetUsageRights[index][key]) && !isExcludedKey(key)) {
          const firstValue = firstAssetUsageRights[index][key].join(',');
          const secondValue = right[key].join(',');
          if (firstValue !== secondValue) {
            firstAssetUsageRights[index][key] = [MULTI_VALUE];
          }
        } else {
          if (firstAssetUsageRights[index][key] !== right[key] && !isExcludedKey(key)) {
            firstAssetUsageRights[index][key] = MULTI_VALUE;
          }
        }
      });
    }
  }

  /**
   * Updates unmatched buyout rights between the first asset's usage rights and the provided usage rights
   * @param firstAssetUsageRights
   * @param oriModifiedBuyoutRightsObj
   * @param restModBuyoutRightsObj
   */
  updateMatchedNUnmatchedBuyoutRights(firstAssetUsageRights: AnyType, oriModifiedBuyoutRightsObj: AnyType, restModBuyoutRightsObj: AnyType): void {
    // Iterate through each key in the original modified buyout rights object
    for (const key in oriModifiedBuyoutRightsObj) {
      // Find the index of the object in the first asset's usage rights with the same buyout rights title as the key
      const index = firstAssetUsageRights.findIndex((obj: AnyType) => obj.buyoutRightsTitle === key);
      // Update the similar and different buyout maps based on the buyout rights of the original and modified buyout rights objects
      this.updateBuyoutMaps(oriModifiedBuyoutRightsObj, restModBuyoutRightsObj, key);
      // Set the buyout rights and unmatched buyout rights of the object in the first asset's usage rights based on the similar and different buyout maps
      firstAssetUsageRights[index]['buyoutRights'] = Array.from(this.buyoutRightsTypes[key].similarBuyout.values());
      firstAssetUsageRights[index]['unmatchedBuyoutRights'] = Array.from(this.buyoutRightsTypes[key].differentBuyout.values());
    }
  }

  /**
   * Updates the buyout maps by iterating through each buyout right in the first buyout rights object
   * and comparing it with the second buyout rights object. If a matching license region key is found, the buyout right
   * is added to the similarBuyout map, otherwise it is added to the differentBuyout map.
   */
  updateBuyoutMaps(oriModifiedBuyoutRightsObj: AnyType, restModBuyoutRightsObj: AnyType, key: string): void {
    const oriBuyoutRights: AnyType[] = oriModifiedBuyoutRightsObj[key]?.buyoutRights;
    const restBuyoutRights: AnyType[] = restModBuyoutRightsObj[key]?.buyoutRights;
    let restBuyoutRightsIndex = -1;
    // Iterate through each buyout right in the first buyout rights object
    if (oriBuyoutRights) {
      oriBuyoutRights.forEach((oriBuyout) => {
        restBuyoutRightsIndex = restBuyoutRights && restBuyoutRights.findIndex((buyout) => buyout.licenseRegionKey === oriBuyout.licenseRegionKey);
        if (restBuyoutRightsIndex !== -1) {
          if (!this.buyoutRightsTypes[key].similarBuyout.has(oriBuyout.licenseRegionKey))
            this.buyoutRightsTypes[key].similarBuyout.set(oriBuyout.licenseRegionKey, oriBuyout);
          if (oriBuyout.expiryDate !== restBuyoutRights[restBuyoutRightsIndex].expiryDate) oriBuyout.expiryDate = MULTI_VALUE;
        } else {
          if (!this.buyoutRightsTypes[key].differentBuyout.has(oriBuyout.licenseRegionKey))
            this.buyoutRightsTypes[key].differentBuyout.set(oriBuyout.licenseRegionKey, oriBuyout);
        }
      });
    }

    // Iterate through each buyout right in the second buyout rights object
    if (restBuyoutRights) {
      restBuyoutRights.forEach((modBuyout) => {
        const orgBuyoutRightsIndex = oriBuyoutRights.findIndex((buyout) => buyout.licenseRegionKey === modBuyout.licenseRegionKey);
        if (orgBuyoutRightsIndex !== -1) {
          if (!this.buyoutRightsTypes[key].similarBuyout.has(modBuyout.licenseRegionKey))
            this.buyoutRightsTypes[key].similarBuyout.set(modBuyout.licenseRegionKey, oriBuyoutRights[orgBuyoutRightsIndex]);
          if (modBuyout.expiryDate !== oriBuyoutRights[orgBuyoutRightsIndex].expiryDate) modBuyout.expiryDate = MULTI_VALUE;
        } else {
          if (!this.buyoutRightsTypes[key].differentBuyout.has(modBuyout.licenseRegionKey))
            this.buyoutRightsTypes[key].differentBuyout.set(modBuyout.licenseRegionKey, modBuyout);
        }
      });
    }
  }

  /**
   * Returns an array of buyout right fields for the given current buyout type
   * @param currentBuyoutType
   * @returns
   */
  getBuyoutRightFields(currentBuyoutType: string): AnyType[] {
    // Filter the buyout rights fields based on the current buyout type and exclude certain form field names
    return buyoutRightsFields(this.brandingKey)
      .filter((field) => field.linkableArray?.includes(currentBuyoutType))
      .filter((field) => !['date-label'].includes(field.formFieldName));
  }

  /**
   * Returns an array of buyout right fields for the given current buyout type for SEAT
   * @param currentBuyoutType
   * @returns
   */
  getBuyoutRightFieldsSEAT(currentBuyoutType: string): AnyType[] {
    // Filter the buyout rights fields based on the current buyout type and exclude certain form field names
    return buyoutRightsFieldsSeat(this.brandingKey)
      .filter((field) => field.linkableArray?.includes(currentBuyoutType))
      .filter((field) => !['date-label'].includes(field.formFieldName));
  }

  updateSelectedItems(type: string, assets: AssetUIModel[], checked: boolean) {
    const selectedItems = JSON.parse(sessionStorage[AppSettings.SESSION_STORAGE_KEY]);
    const currentTypeIndex = selectedItems.findIndex((obj: { type: string }) => obj.type === type);
    assets.forEach((asset: AssetUIModel) => {
      const itemIndex = selectedItems[currentTypeIndex].items.findIndex((item: AnyType) => item._id === asset._id);
      if (asset.isAddedToBasket && itemIndex === -1) {
        selectedItems[currentTypeIndex].items.push(asset);
      } else {
        const itemsFiltered = selectedItems[currentTypeIndex].items.filter((item: AnyType) => {
          return item._id !== asset._id;
        });
        selectedItems[currentTypeIndex].items = itemsFiltered;
      }
    });
    if (checked) {
      selectedItems[currentTypeIndex].selectedAll = true;
    } else {
      selectedItems[currentTypeIndex].selectedAll = false;
    }

    sessionStorage.setItem(AppSettings.SESSION_STORAGE_KEY, JSON.stringify(selectedItems));
  }

  /**
   * Sets the asset view based on the provided gridSettingsKeySuffix and currentSize.
   * It returns the calculated size based on the grid configuration and current screen size.
   * @param gridSettingsKeySuffix
   * @param currentSize
   * @returns
   */
  setAssetView(gridSettingsKeySuffix: string, currentSize?: ScreenSize): number {
    // Retrieve the grid configuration using the provided gridSettingsKeySuffix
    const gridSettings = this.assetViewTypeService.getGridConfig(gridSettingsKeySuffix);
    // Calculate and return the size based on the grid configuration and current screen size
    return this.calculateSize(gridSettings, currentSize);
  }

  /**
   * Calculates the size based on the provided gridSettings and currentSize.
   * It returns the calculated size based on the grid configuration and current screen size.
   * @param gridSettings
   * @param currentSize
   * @returns
   */
  calculateSize(gridSettings: GridConfig, currentSize?: ScreenSize): number {
    // Check if the grid should be displayed in large mode without a sidebar
    if (gridSettings.showGridViewLarge && !gridSettings.showGridViewSidebar) {
      return this.getSizeForLargeNoSidebar(currentSize);
    }
    // Check if the grid should be displayed in small mode without a sidebar
    if (gridSettings.showGridViewSmall && !gridSettings.showGridViewSidebar) {
      return this.getSizeForSmallNoSidebar(currentSize);
    }
    // Check if the grid should be displayed in small mode with a sidebar
    if (gridSettings.showGridViewSidebar && gridSettings.showGridViewSmall) {
      return this.getSizeForSmallWithSidebar(currentSize);
    }
    // Check if the grid should be displayed in large mode with a sidebar
    if (gridSettings.showGridViewSidebar && gridSettings.showGridViewLarge) {
      return this.getSizeForLargeWithSidebar(currentSize);
    }
    // Default size if no conditions match
    return 0;
  }

  /**
   * Returns the size for large mode without a sidebar based on the current screen size.
   * @param currentSize
   * @returns
   */
  getSizeForLargeNoSidebar(currentSize?: ScreenSize): number {
    // Return the size based on the current screen size
    switch (currentSize?.name) {
      case 'xl':
      case 'lg':
        return 8;
      case 'md':
        return 4;
      default:
        return 8;
    }
  }

  /**
   * Returns the size for small mode without a sidebar based on the current screen size.
   * @param currentSize
   * @returns
   */
  getSizeForSmallNoSidebar(currentSize?: ScreenSize): number {
    // Return the size based on the current screen size
    switch (currentSize?.name) {
      case 'lg':
        return 3;
      case 'md':
        return 2;
      default:
        return 4;
    }
  }
  /**
   * Returns the size for small mode with a sidebar based on the current screen size.
   * @param currentSize
   * @returns
   */
  getSizeForSmallWithSidebar(currentSize?: ScreenSize): number {
    // Return the size based on the current screen size
    switch (currentSize?.name) {
      case 'xl':
        return 3;
      case 'lg':
        return 2;
      case 'md':
        return 1;
      default:
        return 3;
    }
  }
  /**
   * Returns the size for large mode with a sidebar based on the current screen size.
   * @param currentSize
   * @returns
   */
  getSizeForLargeWithSidebar(currentSize?: ScreenSize): number {
    // Return the size based on the current screen size
    switch (currentSize?.name) {
      case 'xl':
      case 'lg':
        return 6;
      case 'md':
        return 4;
      default:
        return 6;
    }
  }

  processExportExcel(projectionData?: ProjectionDataModel, domainFlag?: boolean) {
    // Download report of the selected assets
    const basketIDs = this.assetBasketService.assetIdsFromMyBasket;
    this.filterObject['filters']['_id'] = {
      $in: basketIDs,
    };

    let filterOptions: ProjectionDataModel = {};
    if (projectionData) {
      filterOptions = projectionData;
      filterOptions['domainFlag'] = domainFlag;
    }
    const filter = JSON.stringify(this.filterObject.filters);
    const process$ = this.exportExcelApiService.exportsControllerExportExcel({
      type: ExportExcelRequest.TypeEnum.Assets,
      filter,
      fileName: this.env.exportMetadataDownloads?.fileName,
      filterOptions: JSON.stringify(filterOptions),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    this.backgroundProcessService.addExportProcess(process$, ExportExcelRequest.TypeEnum.Assets);
  }

  exportAssets(showEmptyState: boolean) {
    if (showEmptyState) return;

    const config = this.getExportConfig(this.brandingKey);

    if (!config) {
      // Fallback when brandingKey is not handled
      this.processExportExcel(undefined);
      return;
    }

    const dialogRef = this.dialog.open<SelectExportMetadataComponent>(SelectExportMetadataComponent, {
      width: '40%',
      height: '80%',
      data: {
        mandatoryField: config.mandatoryField,
        nonMandatoryField: config.nonMandatoryField,
      },
    });

    dialogRef.disableClose = true;

    dialogRef.afterClosed().subscribe(async (excelData: ProjectionDataModel) => {
      if (!excelData) return;

      const processedData = config.transformExcelData ? config.transformExcelData(excelData) : excelData;

      this.processExportExcel(processedData, config.shouldEnableDomain?.(processedData) ?? true);
    });
  }

  private getExportConfig(brandingKey: CustomClients): {
    mandatoryField: MetadataModel[];
    nonMandatoryField: MetadataModel[];
    transformExcelData?: (data: ProjectionDataModel) => ProjectionDataModel;
    shouldEnableDomain?: (data: ProjectionDataModel) => boolean;
  } | null {
    switch (brandingKey) {
      case CustomClients.Standard:
        return {
          mandatoryField: MandatoryField,
          nonMandatoryField: NonMandatoryField,
          transformExcelData: (data) => {
            if (data.buyoutRightsFields?.length && data.includedFields) {
              data.includedFields['makeBuyoutRights'] = 1;
            }
            return data;
          },
        };

      case CustomClients.Vorwerk:
        return {
          mandatoryField: VorwerkMandatoryField,
          nonMandatoryField: VorwerkNonMandatoryField,
          transformExcelData: (data) => {
            if (data.buyoutRightsFields?.length && data.includedFields) {
              data.includedFields['usageRights'] = 1;
            }
            return data;
          },
          shouldEnableDomain: (data) => data.includedFields?.['domains'] !== 1, // Only enable option if domains not selected
        };

      case CustomClients.SEAT:
        return {
          mandatoryField: SeatMandatoryField,
          nonMandatoryField: SeatNonMandatoryField,
          transformExcelData: (data) => {
            if (data.buyoutRightsFields?.length && data.includedFields) {
              data.includedFields['usageRights'] = 1;
            }
            return data;
          },
          shouldEnableDomain: (data) => data.includedFields?.['domains'] !== 1,
        };

      default:
        return null;
    }
  }

  buyoutRightsConfirmationDialog(uploadedAssets: AssetUIModel[]) {
    this.confirmDialog
      .open({
        title: $localize`Add buyout rights for uploaded assets`,
        message: $localize`You can add buyout rights for individual assets later.`,
        confirmCaption: $localize`:dialog button:Add`,
        confirmCaptionIcon: 'add_box',
        confirmCaptionCssStyle: '!justify-start',
      })
      .pipe(
        switchMap((data) => {
          if (data) {
            return this.buyoutUtilsService.assetBuyoutRightsDialogs(uploadedAssets);
          }
          return EMPTY;
        }),
      )
      .subscribe((update) => {
        if (update) {
          this.assetCommonService.assetDataSubject$.next(update.updatedAsset);
        }
      });
  }

  updateFormData(event: AnyType, filterAssetFormData: FilterFormDataModel, assetSidebar?: AssetsSidebarComponent, sharingClientFlag?: boolean) {
    if (event?.formControlName) {
      switch (this.brandingKey) {
        case CustomClients.Arval:
          this.updateArvalFormData(event.formControlName, filterAssetFormData);
          break;
        case CustomClients.Vorwerk:
          this.updateVorwerkFormData(event, filterAssetFormData, assetSidebar, sharingClientFlag);
          break;
        case CustomClients.SEAT:
          this.updateSEATFormData(event, filterAssetFormData);
      }
    } else {
      this.resetFormFields(filterAssetFormData);
    }
  }

  updateSEATFormData(event: AnyType, filterAssetFormData: FilterFormDataModel) {
    const fieldData = {
      formGroupField: {
        formFields: filterAssetFormData.formFields,
      },
      formFieldName: event.formControlName,
      ...event,
    };
    switch (event.formControlName) {
      case 'brandMarke':
        this.productBrandSelectionService.updateOnBrandMarkeSelectionSEAT(fieldData, undefined, true);
        break;
      case 'brandModel':
        this.productBrandSelectionService.updateOnBrandModelSelectionSEAT(fieldData, undefined, true);
        break;
    }
  }

  updateArvalFormData(formControlName: string, filterAssetFormData: FilterFormDataModel) {
    this.assetCommonService.updateArvalFormData(formControlName, filterAssetFormData);
  }

  updateVorwerkFormData(event: AnyType, filterAssetFormData: FilterFormDataModel, assetSidebar?: AssetsSidebarComponent, sharingClientFlag?: boolean) {
    const sidebarFilters = !!assetSidebar;
    const fieldData = {
      formGroupField: {
        formFields: filterAssetFormData.formFields,
      },
      formFieldName: event.formControlName,
      ...event,
    };

    switch (event.formControlName) {
      case 'brand':
        this.productBrandSelectionService.updateOnBrandSelection(
          fieldData,
          assetSidebar?.filterForms.filterForm as AbstractControl<AnyType, AnyType>,
          sidebarFilters,
          sharingClientFlag,
        );
        break;
      case 'product':
        this.productBrandSelectionService.updateOnProductSelection(
          fieldData,
          assetSidebar?.filterForms.filterForm as AbstractControl<AnyType, AnyType>,
          sidebarFilters,
          sharingClientFlag,
        );
        break;
      case 'plannedForDeletion':
        this.updatePlannedForDeletion(fieldData, event.event.checked);
        break;
      case 'status':
        this.updateStatus(fieldData, fieldData.event.value);
        break;
      case 'agency':
        this.updateAgency(fieldData, event.event.value);
        break;
      case 'category':
        this.updateOnCategorySelection(fieldData, assetSidebar?.filterForms.filterForm as AbstractControl<AnyType, AnyType>, sidebarFilters, sharingClientFlag);
        break;
    }
  }

  updateOnCategorySelection(fieldData: AnyType, formControl?: AbstractControl<AnyType, AnyType>, sidebarFilters = false, sharingClientFlag?: boolean) {
    const selectedCategory = fieldData.event.value._id;
    const formFields = fieldData.formGroupField.formFields;
    const productFormFieldIndex = formFields.findIndex((field: AnyType) => field.formFieldName === 'product');
    const subcategoryFormFieldIndex = formFields.findIndex((field: AnyType) => field.formFieldName === 'subcategory');
    if (productFormFieldIndex !== -1 && selectedCategory === 'product' && (sidebarFilters || sharingClientFlag)) {
      formFields[productFormFieldIndex].visible = true;
    } else {
      formFields[productFormFieldIndex].visible = false;
      formControl?.get('product')?.setValue(null);
      formFields[subcategoryFormFieldIndex].visible = false;
      formControl?.get('subcategory')?.setValue(null);
    }
  }

  updatePlannedForDeletion(fieldData: AnyType, checked: boolean) {
    const formFields = fieldData.formGroupField.formFields;
    ['plannedForDeletion-label', 'plannedForDeletion_from', 'plannedForDeletion_to'].forEach((item) => {
      const index = formFields.findIndex((field: AnyType) => field.formFieldName === item);
      if (index !== -1) {
        formFields[index].visible = checked;
      }
    });
  }

  updateStatus(fieldData: AnyType, value: AnyType) {
    const isApproved = Array.isArray(value) ? value.some((val: AnyType) => val.id === 'approved') : false;
    const formFields = fieldData.formGroupField.formFields;
    ['publishedDate-label', 'publishedDate_from', 'publishedDate_until'].forEach((item) => {
      const index = formFields.findIndex((field: AnyType) => field.formFieldName === item);
      if (index !== -1) {
        formFields[index].visible = isApproved;
      }
    });
  }

  updateAgency(fieldData: AnyType, value: AnyType) {
    if (!value) {
      const formFields = fieldData.formGroupField.formFields;
      const formFieldIndex = formFields.findIndex((field: AnyType) => field.formFieldName === 'client');
      if (formFieldIndex !== -1) formFields[formFieldIndex].options = [];
    }
  }

  resetFormFields(filterAssetFormData: FilterFormDataModel): void {
    switch (this.brandingKey) {
      case CustomClients.Arval:
        this.resetCustomFields(filterAssetFormData, ['channel', 'type']);
        break;
      case CustomClients.Vorwerk:
        this.resetCustomFields(filterAssetFormData, ['product', 'subcategory']);
        this.resetVisibilityFields(filterAssetFormData, [
          'plannedForDeletion-label',
          'plannedForDeletion_from',
          'plannedForDeletion_to',
          'publishedDate-label',
          'publishedDate_from',
          'publishedDate_until',
        ]);
        break;
    }
  }

  /**
   * Rest custom form field (channel and type)
   */
  resetCustomFields(filterAssetFormData: FilterFormDataModel, fields: string[]) {
    // show or hide channel and type based on the patch value
    fields.forEach((item) => {
      this.assetCommonService.findFormFields(item, false, filterAssetFormData);
    });
  }

  /**
   * Hide form fields on reset
   * @param fields
   */
  resetVisibilityFields(filterAssetFormData: FilterFormDataModel, fields: string[]) {
    fields.forEach((item) => {
      const index = filterAssetFormData.formFields.findIndex((field: AnyType) => field.formFieldName === item);
      if (index !== -1) filterAssetFormData.formFields[index].visible = false;
    });
  }

  editDialogConfirmation(assets: AssetUIModel[], editType?: string): AnyType {
    if (!this.shouldCheckAssets()) {
      return of(true);
    }
    let domainMismatchFlag = false;
    if (assets.length > 1 && editType === 'metaData') {
      domainMismatchFlag = this.checkDomainMatching(assets);
    }
    if (this.brandingKey === CustomClients.Vorwerk) {
      if (domainMismatchFlag) {
        this.openDomainMismatchDialog();
        return of(false);
      }
      const checkStatusApprove = this.areAllAssetsApproved(assets);

      if (checkStatusApprove) {
        return this.openAssetEditDialog();
      }
    } else {
      return of({ domainMatching: (!domainMismatchFlag).toString() });
    }

    return of(true);
  }

  private shouldCheckAssets(): boolean {
    return this.brandingKey === CustomClients.Vorwerk || this.brandingKey === CustomClients.SEAT;
  }

  private areAllAssetsApproved(assets: AssetUIModel[]): boolean {
    return assets.every((asset) => asset.status === Asset.StatusEnum.Approved);
  }

  private openDomainMismatchDialog(): void {
    this.confirmDialog.open({
      title: $localize`Domains are not matching`,
      message: $localize`For the selected assets the domain specifications are not matching. \n
      Please select assets that share the same domain specifications in order to multi edit them.`,
      cancelCaption: false,
    });
  }

  private openAssetEditDialog(): Observable<boolean> {
    return this.confirmDialog.open({
      title: $localize`Asset edit`,
      message: $localize`Are you sure you want to edit? The assets will be temporarily not visible in the Media Library.`,
    });
  }

  transformDomainsToString(domain: AnyType) {
    let domainKeyOrder: string[] = [];
    if (this.brandingKey === CustomClients.Vorwerk) {
      domainKeyOrder = ['agency', 'client', 'brand', 'category', 'product', 'subcategory'];
    } else if (this.brandingKey === CustomClients.SEAT) {
      domainKeyOrder = ['agency', 'client', 'brand', 'brandMarke', 'brandModel'];
    }
    let domainKey = '';
    domainKeyOrder.forEach((key: string) => {
      if (domain[key]) {
        domainKey += domain[key] + '.';
      }
    });
    return domainKey;
  }

  checkDomainMatching(selectedAssets: AssetUIModel[]) {
    let domainMismatchingFlag = false;
    const formFieldName = 'domains';
    const finalAssetObject = selectedAssets[0];
    if (selectedAssets.length > 1 && finalAssetObject[formFieldName]) {
      const domainsCount = finalAssetObject[formFieldName].length;
      const assetDomainsArray: AnyType = {};
      selectedAssets.forEach((asset: AnyType) => {
        if (!asset.domains || asset.domains.length != domainsCount) {
          domainMismatchingFlag = true;
        } else {
          asset.domains.forEach((domain: AnyType) => {
            const domainKey = this.transformDomainsToString(domain);
            if (!assetDomainsArray[asset._id]) assetDomainsArray[asset._id] = [];
            assetDomainsArray[asset._id].push(domainKey);
          });
        }
      });
      if (domainMismatchingFlag) {
        return domainMismatchingFlag;
      } else {
        if (Object.keys(assetDomainsArray).length > 0) {
          domainMismatchingFlag = this.validateMatchingString(assetDomainsArray);
        }
      }
    }
    return domainMismatchingFlag;
  }

  validateMatchingString(data: AnyType) {
    // Create a set to store all unique strings
    const allStringsSet = new Set();
    // Add all strings from all arrays into the set
    for (const key in data) {
      for (const str of data[key]) {
        allStringsSet.add(str);
      }
    }
    // Check each array to ensure all strings exist in the set
    for (const value of allStringsSet) {
      for (const key1 in data) {
        if (!data[key1].includes(value)) {
          return true;
        }
      }
    }
    return false;
  }
}
